import React, { useMemo, useState } from 'react';
import { sendSlackNotification } from '../utils/slackNotifications';
import useSeo from '../hooks/useSeo';
import '../styles/pages/Legal.css';
import '../styles/pages/AccountDeletion.css';

function AccountDeletion() {
  const seoConfig = useMemo(() => {
    const title = 'Account and Data Deletion | BloomNav';
    const description =
      'Request deletion of your BloomNav account and the data we hold, including project material and portal access.';
    return {
      title,
      description,
      canonicalPath: '/account-deletion',
      ogType: 'website',
      ogImage: '/logo.png',
      ogImageAlt: 'BloomNav',
      robots: 'noindex, follow'
    };
  }, []);

  useSeo(seoConfig);

  const [formData, setFormData] = useState({
    email: '',
    companyName: '',
    phone: '',
    reason: 'project-complete',
    additionalInfo: '',
    confirmation: false
  });

  const [isSubmitted, setIsSubmitted] = useState(false);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prevData) => ({
      ...prevData,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    await sendSlackNotification(formData, 'deletion');
    setIsSubmitted(true);
  };

  return (
    <div className="legal-page ad-page">
      <div className="legal-head">
        <span className="legal-kicker">Your data</span>
        <h1 className="legal-title">
          Account and <em>data deletion.</em>
        </h1>
        <p className="legal-sub">
          Ask us to close your account and remove the material we hold for you. We will confirm what
          gets deleted and what we are required to keep before anything is removed.
        </p>
      </div>

      <div className="ad-layout">
        <div className="ad-aside">
          <h2>What we delete</h2>
          <p>
            When a request is approved we remove your portal login, the contact details we hold for
            you, and the project material you shared with us: files, designs, documents, sample data,
            and stored credentials.
          </p>

          <h2>What we have to keep</h2>
          <p>
            We keep invoices and financial records for as long as tax and company law require, and we
            keep signed agreements. Backups made before your request expire on their normal schedule.
          </p>
          <p>
            If we host or maintain software for you, deleting your account does not delete that
            system. Tell us in the form below if you want the live product and its data removed too,
            and we will confirm the impact before acting.
          </p>

          <h2>How it works</h2>
          <ol className="ad-steps">
            <li>
              <strong>You send the request</strong>
              We acknowledge by email within 3 business days.
            </li>
            <li>
              <strong>We confirm the scope</strong>
              We tell you exactly what will be deleted and what we must retain.
            </li>
            <li>
              <strong>14-day grace period</strong>
              You can cancel the request during this time by replying to our email.
            </li>
            <li>
              <strong>Deletion within 30 days</strong>
              We remove the data and send written confirmation.
            </li>
          </ol>

          <div className="ad-note">
            <p>
              <strong>Handover first.</strong> If a project is still active, or you have not yet taken
              a copy of your code, designs, or data, ask us for a handover before requesting deletion.
              We cannot recover material once it is removed.
            </p>
            <p>
              <strong>End users of an app we built:</strong> contact the company that owns the app.
              They control that data and we act on their instructions.
            </p>
          </div>
        </div>

        {isSubmitted ? (
          <div className="ad-success">
            <div className="ad-success-mark" aria-hidden="true">
              <svg viewBox="0 0 24 24" fill="none">
                <path
                  d="m5 13 4 4L19 7"
                  stroke="currentColor"
                  strokeWidth="2.2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </div>
            <h2>Request received</h2>
            <p>
              Thanks. We have your deletion request and will reply within 3 business days to confirm
              what will be removed and what we are required to keep.
            </p>
            <p>
              Your data will be deleted within 30 days of that confirmation. You have 14 days to
              cancel the request by replying to our email.
            </p>
            <p>
              If you need a copy of anything before deletion, tell us now at
              {' '}
              <strong>support@bloomnav.com</strong>.
            </p>
          </div>
        ) : (
          <div className="ad-card">
            <div className="ad-card-head">
              <h2>Request deletion</h2>
              <p>Use the email address linked to your account so we can find the right records.</p>
            </div>

            <form className="ad-form" onSubmit={handleSubmit}>
              <div className="ad-field">
                <label htmlFor="email">Your email address</label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  placeholder="you@company.com"
                  required
                />
              </div>

              <div className="ad-field">
                <label htmlFor="companyName">Company name</label>
                <input
                  type="text"
                  id="companyName"
                  name="companyName"
                  value={formData.companyName}
                  onChange={handleChange}
                  placeholder="Your company"
                  required
                />
              </div>

              <div className="ad-field">
                <label htmlFor="phone">Phone number</label>
                <input
                  type="tel"
                  id="phone"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  placeholder="+91 98765 43210"
                  required
                />
              </div>

              <div className="ad-field">
                <label htmlFor="reason">Reason for the request</label>
                <select id="reason" name="reason" value={formData.reason} onChange={handleChange}>
                  <option value="project-complete">Project is complete and handed over</option>
                  <option value="no-longer-working">No longer working with BloomNav</option>
                  <option value="data-removal">Data removal request only</option>
                  <option value="privacy-request">Privacy or compliance request</option>
                  <option value="other">Other</option>
                </select>
              </div>

              <div className="ad-field">
                <label htmlFor="additionalInfo">What should be deleted?</label>
                <textarea
                  id="additionalInfo"
                  name="additionalInfo"
                  value={formData.additionalInfo}
                  onChange={handleChange}
                  placeholder="Tell us what to remove: portal access, project files, hosted systems, or everything. Mention anything you still need a copy of."
                  rows="4"
                ></textarea>
              </div>

              <div className="ad-confirm">
                <input
                  type="checkbox"
                  id="confirmation"
                  name="confirmation"
                  checked={formData.confirmation}
                  onChange={handleChange}
                  required
                />
                <label htmlFor="confirmation">
                  I understand that deletion is permanent after the 14-day grace period, and that I
                  am responsible for taking a copy of anything I still need.
                </label>
              </div>

              <button type="submit" className="ad-submit" disabled={!formData.confirmation}>
                Send deletion request
              </button>

              <p className="ad-form-foot">
                We will reply within 3 business days to confirm the scope before deleting anything.
              </p>
            </form>
          </div>
        )}
      </div>

      <div className="ad-help">
        <h2>Not sure yet?</h2>
        <p>
          If something is not working, or you have questions about what we hold, talk to us first at{' '}
          <a href="mailto:support@bloomnav.com">support@bloomnav.com</a> or through our{' '}
          <a href="/contact">contact page</a>. Deletion is permanent, so we are happy to walk through
          the options before you decide.
        </p>
      </div>
    </div>
  );
}

export default AccountDeletion;
